import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  api,
  ApiError,
  type AdminPage,
  type QueueVariableOut,
  type QueueVariableCreate,
  type QueueVariableUpdate,
} from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/admin/DataTable";
import { CrudDrawer, type FieldDef, type FieldValues } from "@/components/admin/CrudDrawer";
import { Button } from "@/components/ui/Button";
import { SelectField } from "@/components/ui/SelectField";
import { PlusIcon } from "@/components/ui/icons";

/** Sentinel for the "Global (all queues)" option → queue_id = null / global_only. */
const GLOBAL_SENTINEL = "global";

type QueueScope = typeof GLOBAL_SENTINEL | number;

/**
 * List queue variables with server-side filter.
 * adminCrud.list does not forward queue_id/global_only, so we call request directly.
 */
function listScopedVariables(
  scope: QueueScope,
  signal?: AbortSignal,
): Promise<AdminPage<QueueVariableOut>> {
  const query =
    scope === GLOBAL_SENTINEL
      ? { global_only: true, page_size: 500 }
      : { queue_id: scope, page_size: 500 };
  return api.request<AdminPage<QueueVariableOut>>("GET", "/api/v1/admin/queue-variables", {
    query,
    signal,
  });
}

export function QueueVariablesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [scope, setScope] = useState<QueueScope>(GLOBAL_SENTINEL);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<QueueVariableOut | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const queuesQ = useQuery({
    queryKey: ["admin", "queues", "for-queue-variables"],
    queryFn: ({ signal }) => api.adminQueues.list({ valid: "valid", pageSize: 500 }, signal),
    staleTime: 5 * 60 * 1000,
  });

  const varsQ = useQuery({
    queryKey: ["admin", "queue-variables", scope],
    queryFn: ({ signal }) => listScopedVariables(scope, signal),
  });

  const physicalQ = useQuery({
    queryKey: ["admin", "queue-physical-variables", scope],
    queryFn: ({ signal }) => api.listQueuePhysicalVariables(scope as number, signal),
    enabled: scope !== GLOBAL_SENTINEL,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "queue-variables"] });

  const createM = useMutation({
    mutationFn: (body: QueueVariableCreate) => api.adminQueueVariables.create(body),
    onSuccess: async () => {
      setDrawerOpen(false);
      await invalidate();
    },
  });

  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: number; body: QueueVariableUpdate }) =>
      api.adminQueueVariables.update(id, body),
    onSuccess: async () => {
      setDrawerOpen(false);
      await invalidate();
    },
  });

  // Hard DELETE (no soft-valid flag on this table); adminCrud.deactivate maps to DELETE.
  const deleteM = useMutation({
    mutationFn: (id: number) => api.adminQueueVariables.deactivate(id),
    onSuccess: () => invalidate(),
  });

  const openCreate = () => {
    setEditing(null);
    setFormError(null);
    setDrawerOpen(true);
  };

  const openEdit = (row: QueueVariableOut) => {
    setEditing(row);
    setFormError(null);
    setDrawerOpen(true);
  };

  const queueIdForCreate = scope === GLOBAL_SENTINEL ? null : scope;

  const handleSubmit = async (values: FieldValues) => {
    setFormError(null);
    const name = String(values.name ?? "").trim();
    const value = String(values.value ?? "");
    try {
      if (editing) {
        await updateM.mutateAsync({
          id: editing.id,
          body: { name, value },
        });
      } else {
        await createM.mutateAsync({
          name,
          value,
          queue_id: queueIdForCreate,
        });
      }
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t("admin.form.genericError"));
      throw err;
    }
  };

  const columns: DataTableColumn<QueueVariableOut>[] = [
    {
      key: "name",
      header: t("admin.queueVariables.name"),
      mono: true,
      render: (r) => r.name,
    },
    {
      key: "value",
      header: t("admin.queueVariables.value"),
      render: (r) => r.value ?? "",
    },
  ];

  const fields: FieldDef[] = [
    {
      name: "name",
      label: t("admin.queueVariables.name"),
      type: "text",
      required: true,
      mono: true,
      helpText: t("admin.queueVariables.nameHelp"),
    },
    {
      name: "value",
      label: t("admin.queueVariables.value"),
      type: "textarea",
      rows: 3,
    },
  ];

  const rows = varsQ.data?.items ?? [];
  const queues = queuesQ.data?.items ?? [];
  const physical = physicalQ.data ?? [];
  const showPhysical = scope !== GLOBAL_SENTINEL;

  return (
    <div className="space-y-3 p-4" data-testid="admin-queue-variables-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-xl font-semibold text-ink">
          {t("admin.queueVariables.title_plural")}
        </h1>
        <Button
          variant="primary"
          size="sm"
          data-testid="admin-queue-variables-new"
          onClick={openCreate}
          aria-label={t("admin.queueVariables.new")}
          title={t("admin.queueVariables.new")}
          className="!px-2"
        >
          <PlusIcon className="text-[16px]" />
        </Button>
      </div>

      <label className="flex flex-wrap items-center gap-2 text-sm text-ink">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
          {t("admin.queueVariables.queue")}
        </span>
        <SelectField
          items={[
            { value: GLOBAL_SENTINEL, label: t("admin.queueVariables.global") },
            ...queues.map((q) => ({ value: String(q.id), label: q.name })),
          ]}
          value={scope === GLOBAL_SENTINEL ? GLOBAL_SENTINEL : String(scope)}
          onChange={(v) => setScope(v === GLOBAL_SENTINEL ? GLOBAL_SENTINEL : Number(v))}
          testId="admin-queue-variables-queue-select"
          className="w-auto min-w-[16rem]"
        />
      </label>

      <p className="text-xs text-muted" data-testid="admin-queue-variables-hint">
        {scope === GLOBAL_SENTINEL
          ? t("admin.queueVariables.globalHint")
          : t("admin.queueVariables.queueHint")}
      </p>

      {showPhysical && (
        <section
          className="rounded-lg border border-hairline bg-surface-subtle/40 p-3"
          data-testid="admin-queue-variables-physical"
        >
          <h2 className="text-sm font-semibold text-ink">
            {t("admin.queueVariables.physicalTitle")}
          </h2>
          <p className="mt-1 text-xs text-muted">{t("admin.queueVariables.physicalHint")}</p>
          {physicalQ.isLoading ? (
            <p className="mt-2 text-xs text-muted">{t("common.loading")}</p>
          ) : physical.length === 0 ? (
            <p
              className="mt-2 text-xs text-muted"
              data-testid="admin-queue-variables-physical-empty"
            >
              {t("admin.queueVariables.physicalEmpty")}
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-hairline" data-testid="admin-queue-variables-physical-list">
              {physical.map((item) => (
                <li
                  key={item.name}
                  className="flex flex-wrap items-baseline justify-between gap-2 py-1.5 text-sm"
                  data-testid={`admin-queue-variables-physical-${item.name}`}
                >
                  <code className="font-mono text-[12.5px] text-ink">
                    {`<OTRS_QUEUE_${item.name}>`}
                  </code>
                  <span className="text-ink">{item.value || "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        isLoading={varsQ.isLoading}
        emptyLabel={t("admin.queueVariables.empty")}
        onEdit={openEdit}
        onDeactivate={(row) => deleteM.mutate(row.id)}
        testId="admin-queue-variables-table"
      />

      <CrudDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={
          editing
            ? t("admin.form.editTitle", { title: t("admin.queueVariables.title_plural") })
            : t("admin.queueVariables.new")
        }
        fields={fields}
        mode={editing ? "edit" : "create"}
        initialValues={
          editing
            ? { name: editing.name, value: editing.value ?? "" }
            : { name: "", value: "" }
        }
        onSubmit={handleSubmit}
        submitError={formError}
        testIdPrefix="admin-queue-variables-form"
      />
    </div>
  );
}
